import { useState } from "react";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { fmtInr } from "@/components/quotation/LineItemsEditor";

const FIELDS = [
  ["machine_cost", "Machine cost (₹)"], ["amc_per_year", "AMC cost per year (₹)"], ["finance_rate", "Finance rate (% per year)"],
  ["term_months", "Term (months)"], ["margin_pct", "Margin (% on top of cost)"],
];

const Result = ({ title, r, note, testId }) => (
  <div className="rounded-md border border-slate-200 p-3" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
    <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid={`${testId}-amount`}>{fmtInr(r.suggested_monthly)}<span className="text-xs font-normal text-slate-500"> / month</span></div>
    <div className="text-[11px] text-slate-500 mt-1">{note}</div>
  </div>
);

// Admin-only pricing helper: suggests a figure, nothing is saved to the quotation.
export function RentalCalculator() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ machine_cost: "", amc_per_year: "", finance_rate: "12", term_months: "36", margin_pct: "20" });
  const [res, setRes] = useState(null);
  const run = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Number(v) || 0]));
      setRes((await api.post("/quotations/rental-calculator", body)).data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="text-sky-700 border-sky-200" data-testid="rental-calculator-btn"><Calculator className="w-3.5 h-3.5 mr-1" /> Pricing calculator</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="rental-calculator-dialog">
          <DialogHeader>
            <DialogTitle>Rental pricing calculator</DialogTitle>
            <DialogDescription>Admin only. Suggests a monthly rental. Type the final figure into the line item yourself. Nothing here is saved or printed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={k === "machine_cost" ? "col-span-2" : ""}>
                <Label className="text-xs text-slate-500">{label}</Label>
                <Input type="number" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`calc-${k}`} />
              </div>
            ))}
          </div>
          <Button onClick={run} disabled={!Number(f.machine_cost) || !Number(f.term_months)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="calc-run">Calculate</Button>
          {res && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Result title="Without buy option" r={res.without_buy_option} testId="calc-without" note={`EMI ${fmtInr(res.without_buy_option.emi)} + AMC ${fmtInr(res.without_buy_option.monthly_amc)}, plus margin`} />
              <Result title="With buy option" r={res.with_buy_option} testId="calc-with" note={`EMI on ${fmtInr(res.with_buy_option.financed_amount)} (machine + AMC), plus margin`} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
